// 3D text -> depth map (static and animated).

import * as THREE from 'three';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import type { Font } from 'three/examples/jsm/loaders/FontLoader.js';
import type { DepthData } from '../types';
import { renderDepth } from './render';

export interface TextParams {
  text: string;
  font: string;
  /** Fraction of the frame the text block may occupy (0.1..1). */
  fill: number;
  /** Extrusion depth relative to the font size. */
  extrude: number;
  /** Bevel size relative to the font size (0 = none). */
  bevel: number;
  /** Rotation in degrees. */
  rotX: number;
  rotY: number;
  rotZ: number;
  /** Depth of the text's back and front (0..1). */
  base: number;
  top: number;
  lineHeight: number;
}

export const DEFAULT_TEXT: TextParams = {
  text: '3D',
  font: 'russo',
  fill: 0.7,
  extrude: 0.35,
  bevel: 0.04,
  rotX: 0,
  rotY: 0,
  rotZ: 0,
  base: 0.45,
  top: 1,
  lineHeight: 1.1,
};

const SIZE = 100;

/** Builds a centred text geometry (units where font size = 100). */
export function buildTextGeometry(font: Font, p: TextParams): THREE.BufferGeometry {
  const lines = (p.text || ' ').split('\n');
  const geoms: THREE.BufferGeometry[] = [];
  const bevel = Math.max(0, p.bevel) * SIZE;
  lines.forEach((line, i) => {
    if (!line.trim()) return;
    const g = new TextGeometry(line, {
      font,
      size: SIZE,
      depth: Math.max(0.01, p.extrude) * SIZE,
      curveSegments: 8,
      bevelEnabled: bevel > 0,
      bevelThickness: bevel,
      bevelSize: bevel * 0.8,
      bevelSegments: 4,
    });
    g.computeBoundingBox();
    const bb = g.boundingBox!;
    g.translate(-(bb.min.x + bb.max.x) / 2, -i * SIZE * p.lineHeight * 1.2, 0);
    geoms.push(g);
  });
  const merged = geoms.length ? mergeGeometries(geoms) : new THREE.BufferGeometry();
  geoms.forEach((g) => g.dispose());
  merged.computeBoundingBox();
  const bb = merged.boundingBox ?? new THREE.Box3(new THREE.Vector3(), new THREE.Vector3());
  const c = new THREE.Vector3();
  bb.getCenter(c);
  merged.translate(-c.x, -c.y, -c.z);
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}

function mergeGeometries(geoms: THREE.BufferGeometry[]): THREE.BufferGeometry {
  // Only positions matter for depth; avoids pulling in BufferGeometryUtils.
  const parts = geoms.map((g) => (g.index ? g.toNonIndexed() : g));
  const total = parts.reduce((n, g) => n + g.attributes.position.count * 3, 0);
  const pos = new Float32Array(total);
  let off = 0;
  for (const g of parts) {
    pos.set(g.attributes.position.array as Float32Array, off);
    off += g.attributes.position.count * 3;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  return out;
}

/** A text mesh fitted into an orthographic frame, ready for (animated) depth renders. */
export interface TextRig {
  scene: THREE.Scene;
  /** Holder for animation transforms; the mesh inside carries the base rotation. */
  pivot: THREE.Group;
  mesh: THREE.Mesh;
  camera: THREE.OrthographicCamera;
  width: number;
  height: number;
  /** Untransformed vertex positions (for deformation presets). */
  rest: Float32Array;
  /** Size of the text block in world units (after fit). */
  size: THREE.Vector3;
}

export function createTextRig(font: Font, p: TextParams, width: number, height: number): TextRig {
  const geometry = buildTextGeometry(font, p);
  const mesh = new THREE.Mesh(geometry);
  const size = new THREE.Vector3();
  geometry.boundingBox!.getSize(size);
  const aspect = width / height;
  const viewH = 2;
  const viewW = viewH * aspect;
  const fill = Math.min(1, Math.max(0.05, p.fill));
  const s = Math.min((viewW * fill) / Math.max(size.x, 1e-3), (viewH * fill) / Math.max(size.y, 1e-3));
  mesh.scale.setScalar(s);
  mesh.rotation.set(
    THREE.MathUtils.degToRad(p.rotX),
    THREE.MathUtils.degToRad(p.rotY),
    THREE.MathUtils.degToRad(p.rotZ),
  );
  const pivot = new THREE.Group();
  pivot.add(mesh);
  const scene = new THREE.Scene();
  scene.add(pivot);
  const camera = new THREE.OrthographicCamera(-viewW / 2, viewW / 2, viewH / 2, -viewH / 2, 0.01, 1000);
  camera.position.set(0, 0, 100);
  camera.lookAt(0, 0, 0);
  camera.updateMatrixWorld(true);
  const rest = new Float32Array(geometry.attributes.position.array as Float32Array);
  return { scene, pivot, mesh, camera, width, height, rest, size: size.multiplyScalar(s) };
}

/** World-space Z extent of the rig in its current pose: [far, near]. */
export function rigZRange(rig: TextRig): [number, number] {
  rig.scene.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(rig.mesh, true);
  return [box.min.z, Math.max(box.max.z, box.min.z + 1e-4)];
}

export function renderRig(rig: TextRig, p: TextParams, zRange: [number, number]): DepthData {
  rig.scene.updateMatrixWorld(true);
  return renderDepth(
    rig.scene,
    rig.camera,
    { mode: 'height', near: zRange[1], far: zRange[0], base: p.base, top: p.top },
    rig.width,
    rig.height,
  );
}

export function disposeRig(rig: TextRig) {
  rig.mesh.geometry.dispose();
}

/** Renders static text to a depth map of the given size. */
export function renderTextDepth(font: Font, p: TextParams, width: number, height: number): DepthData {
  const rig = createTextRig(font, p, width, height);
  const depth = renderRig(rig, p, rigZRange(rig));
  disposeRig(rig);
  return depth;
}
